import { useState } from "react";
import { saveRoutine, deleteRoutine } from "../services/template-service";
import { useNotification } from "../../../context/notification-provider";
import type { RoutineItem, AvailableRoutineItem } from "../types/api-types";

interface UseRoutineManagementProps {
  templateId: number;
  routines: RoutineItem[];
  availableRoutines: AvailableRoutineItem[] | undefined;
  mutate: () => void;
}

export const useRoutineManagement = ({
  templateId,
  routines,
  availableRoutines,
  mutate,
}: UseRoutineManagementProps) => {
  const { showNotification } = useNotification();

  // Add routine state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRoutineId, setSelectedRoutineId] = useState<number | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);

  // Delete routine state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [routineToDelete, setRoutineToDelete] = useState<{
    id: number;
    description: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setSelectedRoutineId(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRoutineId(null);
  };

  const handleSave = async () => {
    if (selectedRoutineId === null) return;

    const selectedRoutine = availableRoutines?.find(
      (r) => r.id === selectedRoutineId,
    );
    if (!selectedRoutine) return;

    setIsSaving(true);
    try {
      const routineData = {
        id: 0,
        templateId,
        routineId: selectedRoutineId,
        description: selectedRoutine.storedProcedure,
      };

      await saveRoutine(routineData);
      mutate();
      showNotification({
        message: "Routine added successfully",
        severity: "success",
      });
      handleCloseDialog();
    } catch (err) {
      showNotification({
        message: `Failed to save routine. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: number, description: string) => {
    setRoutineToDelete({ id, description });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!routineToDelete) return;

    setIsDeleting(true);
    try {
      await deleteRoutine(routineToDelete.id);
      mutate();
      showNotification({
        message: "Routine deleted successfully",
        severity: "success",
      });
      setDeleteConfirmOpen(false);
      setRoutineToDelete(null);
    } catch (err) {
      showNotification({
        message: `Failed to delete routine. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setRoutineToDelete(null);
  };

  // Get available routines that haven't been added yet
  const getAvailableOptions = () => {
    return (
      availableRoutines?.filter(
        (ar) => ar.enabled && !routines.some((r) => r.routineId === ar.id),
      ) ?? []
    );
  };

  return {
    // Add dialog state
    openDialog,
    selectedRoutineId,
    isSaving,
    setSelectedRoutineId,
    handleOpenDialog,
    handleCloseDialog,
    handleSave,
    getAvailableOptions,

    // Delete dialog state
    deleteConfirmOpen,
    routineToDelete,
    isDeleting,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useRoutineManagement;
